import { useState, useEffect, useRef } from "react";
import { ALL_DAYS, TIER_COLORS } from "../utils/tiers";
import { getGroupSchedule, formatRange, parseTime } from "../utils/time";
import { computeStats, isScheduled } from "../utils/stats";
import { getWarnings, groupsTimeOverlap } from "../utils/validation";
import { COACH_COLORS, coachInitials } from "../utils/coachColors";
import { hasAvailabilityConstraints, availabilitySummary, AVAIL_START_OPTIONS, AVAIL_END_OPTIONS } from "../utils/coachAvailability";
import GroupModal from "../components/GroupModal";
import WeekendSessionModal from "../components/WeekendSessionModal";

function fmtHours(h) {
  if (!h) return "0";
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

// Solid color chip with the coach's initials — used both in the list and as
// the profile header avatar.
function CoachAvatar({ coach, size = "w-9 h-9 text-sm" }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-bold text-white flex-shrink-0 ${size}`}
      style={{ backgroundColor: coach.color || "#64748b" }}
    >
      {coachInitials(coach.name)}
    </span>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {COACH_COLORS.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-6 h-6 rounded-full transition-transform ${value === c ? "ring-2 ring-offset-1 ring-gray-800 scale-110" : "hover:scale-110"}`}
          style={{ backgroundColor: c }}
          aria-label={`Use color ${c}`}
        />
      ))}
    </div>
  );
}

// Weekly availability editor (presentational): a master "limit availability"
// toggle plus a row per day (available? + a From/To window). Off by default so
// a coach stays unconstrained until you opt in; when first enabled, every day
// defaults to available across the full schedule so turning it on flags
// nothing until you actually narrow it. All state lives in the parent form.
function AvailabilityEditor({ days, limited, rows, onToggle, onSetRow }) {
  const selCls = "border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400";
  return (
    <div>
      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
        <input type="checkbox" checked={limited} onChange={e => onToggle(e.target.checked)} className="rounded" />
        Limit to specific availability
      </label>
      {!limited && (
        <p className="text-xs text-gray-400 mt-1">Available anytime — sessions are never flagged for this coach.</p>
      )}
      {limited && (
        <div className="mt-2 space-y-1.5">
          {days.map(day => {
            const r = rows[day];
            return (
              <div key={day} className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 w-28 flex-shrink-0 text-xs text-gray-700 cursor-pointer select-none">
                  <input type="checkbox" checked={r.available} onChange={e => onSetRow(day, { available: e.target.checked })} className="rounded" />
                  {day}
                </label>
                {r.available ? (
                  <div className="flex items-center gap-1.5">
                    <select value={r.start} onChange={e => onSetRow(day, { start: e.target.value })} className={selCls}>
                      {AVAIL_START_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <span className="text-gray-400 text-xs">to</span>
                    <select value={r.end} onChange={e => onSetRow(day, { end: e.target.value })} className={selCls}>
                      {AVAIL_END_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 italic">Not available</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Shared add/edit form. `coach` is null when adding.
function CoachForm({ coach, coachCount, days, onSubmit, onCancel }) {
  const [name, setName] = useState(coach?.name || "");
  const [email, setEmail] = useState(coach?.email || "");
  const [phone, setPhone] = useState(coach?.phone || "");
  const [maxHours, setMaxHours] = useState(coach?.maxHours ?? "");
  const [notes, setNotes] = useState(coach?.notes || "");
  const [color, setColor] = useState(coach?.color || COACH_COLORS[coachCount % COACH_COLORS.length]);
  const [availLimited, setAvailLimited] = useState(() => hasAvailabilityConstraints(coach));
  const [availRows, setAvailRows] = useState(() => {
    const hasPrior = hasAvailabilityConstraints(coach);
    const base = {};
    days.forEach(d => {
      const w = coach?.availability?.[d];
      base[d] = {
        available: hasPrior ? !!w : true,
        start: w?.start || "6:00 AM",
        end: w?.end || "10:00 PM",
      };
    });
    return base;
  });

  function setAvailRow(day, patch) {
    setAvailRows(prev => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  }

  function buildAvailability() {
    if (!availLimited) return null;
    const out = {};
    days.forEach(d => {
      if (availRows[d].available) out[d] = { start: availRows[d].start, end: availRows[d].end };
    });
    return out;
  }

  function submit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      maxHours: maxHours === "" ? null : Number(maxHours),
      notes: notes.trim(),
      color,
      availability: buildAvailability(),
    });
  }

  const inputCls = "border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400";
  const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className={labelCls}>Name</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Coach name" className={inputCls} autoFocus />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="coach@email.com" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Max hrs / week</label>
        <input type="number" min="0" value={maxHours} onChange={e => setMaxHours(e.target.value)} placeholder="No cap" className={`${inputCls} w-32`} />
      </div>
      <div>
        <label className={labelCls}>Availability</label>
        <AvailabilityEditor
          days={days}
          limited={availLimited}
          rows={availRows}
          onToggle={setAvailLimited}
          onSetRow={setAvailRow}
        />
      </div>
      <div>
        <label className={labelCls}>Color tag</label>
        <ColorPicker value={color} onChange={setColor} />
      </div>
      <div>
        <label className={labelCls}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Specialties, availability, anything worth remembering…" className={inputCls} />
      </div>
      <div className="flex gap-2 pt-1">
        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors">
          {coach ? "Save changes" : "+ Add coach"}
        </button>
        <button type="button" onClick={onCancel} className="border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm px-4 py-1.5 rounded">Cancel</button>
      </div>
    </form>
  );
}

// One weekday column in the coach's mini week: their sessions that day, stacked
// and time-ordered, colored by tier.
function DayColumn({ day, sessions, overlapIds, onOpenGroup }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 text-center">{day.slice(0, 3)}</div>
      <div className="space-y-1.5">
        {sessions.length === 0 ? (
          <div className="text-[11px] text-gray-300 text-center py-2">—</div>
        ) : (
          sessions.map(g => {
            const tc = TIER_COLORS[g.tier] || TIER_COLORS["No Group"];
            const { startTime, duration } = getGroupSchedule(g);
            const clash = overlapIds.has(g.id);
            return (
              <button
                key={g.id}
                onClick={() => onOpenGroup(g.id)}
                className={`w-full text-left rounded border px-1.5 py-1 transition-shadow ${
                  clash ? "border-red-400 ring-1 ring-red-300 bg-red-50" : `${tc.border} ${tc.bg} hover:ring-1 hover:ring-blue-300`
                }`}
                title={clash ? "Overlaps another of this coach's sessions — click to edit" : "Open group to edit"}
              >
                <div className="text-[11px] font-semibold text-gray-700 leading-tight flex items-center gap-1">
                  {clash && <span className="text-red-600" title="Double-booked">⚠</span>}
                  {formatRange(startTime, duration)}
                </div>
                <div className={`text-[10px] font-medium ${tc.text} truncate`}>{g.tier}</div>
                <div className="text-[10px] text-gray-500 truncate">{g.court || "No court"}{g.kind ? "" : ` · ${g.playerIds.length}/4`}</div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// Compact coach picker used on a group card to reassign/unassign quickly.
function GroupCoachSelect({ group, coaches, onReassign }) {
  return (
    <select
      value={group.coachId || ""}
      onChange={e => onReassign(group.id, e.target.value || null)}
      onClick={e => e.stopPropagation()}
      className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 max-w-[9rem]"
      title="Reassign coach"
    >
      <option value="">Unassigned</option>
      {coaches.map(c => (
        <option key={c.id} value={c.id}>{c.name}</option>
      ))}
    </select>
  );
}

function CoachDetail({ coach, stat, coachGroups, unassignedGroups, spareCoaches = [], coaches, players, onEdit, onDelete, onPlayerClick, onReassignGroup, onOpenGroup }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => setConfirmDelete(false), [coach.id]);

  const playerMap = Object.fromEntries(players.map(p => [p.id, p]));
  const hours = stat?.hours || 0;
  const groupCount = coachGroups.length;
  const capPct = coach.maxHours ? Math.min(100, (hours / coach.maxHours) * 100) : null;

  // Sessions grouped by weekday, time-ordered, for the mini week.
  const byDay = Object.fromEntries(ALL_DAYS.map(day => [
    day,
    coachGroups
      .filter(g => g.day === day)
      .sort((a, b) => (parseTime(getGroupSchedule(a).startTime) || 0) - (parseTime(getGroupSchedule(b).startTime) || 0)),
  ]));

  const idle = groupCount === 0;
  const hasSpareCap = coach.maxHours != null && !idle && hours < coach.maxHours;

  // Group ids that overlap another of THIS coach's sessions (same day/time) —
  // the concrete pairs behind the "double-booked" flag, highlighted red in the
  // weekly grid below.
  const overlapIds = new Set();
  for (let i = 0; i < coachGroups.length; i++) {
    for (let j = i + 1; j < coachGroups.length; j++) {
      if (groupsTimeOverlap(coachGroups[i], coachGroups[j])) {
        overlapIds.add(coachGroups[i].id);
        overlapIds.add(coachGroups[j].id);
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <CoachAvatar coach={coach} size="w-12 h-12 text-lg" />
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-800 truncate">{coach.name}</h2>
            <div className="text-xs text-gray-400 font-mono">{coach.id}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={onEdit} className="text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded px-3 py-1">Edit</button>
          {confirmDelete ? (
            <button onClick={() => onDelete(coach.id)} className="text-sm bg-red-600 hover:bg-red-700 text-white rounded px-3 py-1 font-semibold">Confirm delete</button>
          ) : (
            <button onClick={() => setConfirmDelete(true)} onBlur={() => setConfirmDelete(false)} className="text-sm text-gray-400 hover:text-red-600 border border-gray-300 rounded px-3 py-1">Delete</button>
          )}
        </div>
      </div>

      {/* Contact */}
      {(coach.email || coach.phone) && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {coach.email && <a href={`mailto:${coach.email}`} className="text-blue-600 hover:underline">✉ {coach.email}</a>}
          {coach.phone && <a href={`tel:${coach.phone}`} className="text-gray-600 hover:text-gray-900">☎ {coach.phone}</a>}
        </div>
      )}

      {/* Utilization + warnings */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="text-2xl font-bold text-gray-800">{fmtHours(hours)}h</span>
            <span className="text-sm text-gray-500 ml-2">
              {coach.maxHours != null ? `of ${fmtHours(coach.maxHours)}h/wk` : "no weekly cap"} · {groupCount} group{groupCount !== 1 ? "s" : ""}
            </span>
          </div>
          {capPct != null && <span className={`text-sm font-semibold ${stat?.overCapacity ? "text-red-600" : "text-teal-600"}`}>{Math.round(capPct)}%</span>}
        </div>
        {capPct != null && (
          <div className="bg-gray-100 rounded h-2.5 overflow-hidden">
            <div className={`h-full rounded ${stat?.overCapacity ? "bg-red-500" : "bg-teal-400"}`} style={{ width: `${capPct}%` }} />
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {stat?.overCapacity && (
            <span className="text-xs bg-red-50 text-red-700 border border-red-200 rounded px-2 py-1 font-medium">⚠ Over capacity by {fmtHours(hours - coach.maxHours)}h</span>
          )}
          {stat?.doubleBooked && (
            <span className="text-xs bg-red-50 text-red-700 border border-red-200 rounded px-2 py-1 font-medium">⚠ Double-booked — overlapping sessions are marked red below</span>
          )}
          {idle && (
            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-1 font-medium">Idle — not assigned to any group yet{unassignedGroups.length > 0 ? ". Assign one below." : ""}</span>
          )}
          {hasSpareCap && (
            <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-1 font-medium">{fmtHours(coach.maxHours - hours)}h of free capacity this week</span>
          )}
        </div>
        {stat?.overCapacity && spareCoaches.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            💡 Balance the load — reassign a group (via its coach picker below) to{" "}
            {spareCoaches.slice(0, 3).map((sc, i) => (
              <span key={sc.coach.id}>
                {i > 0 && ", "}
                <span className="font-medium text-gray-700">{sc.coach.name}</span> ({fmtHours(sc.free)}h free)
              </span>
            ))}.
          </p>
        )}
      </div>

      {/* Weekly schedule */}
      <div>
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Weekly schedule</h3>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {ALL_DAYS.map(day => (
            <DayColumn key={day} day={day} sessions={byDay[day]} overlapIds={overlapIds} onOpenGroup={onOpenGroup} />
          ))}
        </div>
      </div>

      {/* Assigned groups */}
      <div>
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Assigned groups ({groupCount})</h3>
        {groupCount === 0 ? (
          <p className="text-sm text-gray-400 italic">No groups yet.</p>
        ) : (
          <div className="space-y-2">
            {coachGroups.map(g => {
              const tc = TIER_COLORS[g.tier] || TIER_COLORS["No Group"];
              const { startTime, duration } = getGroupSchedule(g);
              const roster = g.playerIds.map(pid => playerMap[pid]).filter(Boolean);
              return (
                <div key={g.id} className={`border ${tc.border} rounded-lg p-3`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-xs font-semibold ${tc.text} ${tc.bg} border ${tc.border} rounded px-1.5 py-0.5`}>{g.tier}</span>
                      <span className="text-sm font-medium text-gray-700 truncate">{g.day ? `${g.day} · ${formatRange(startTime, duration)}` : "Unscheduled"}</span>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">{g.court || "No court"} · {roster.length}/4</span>
                  </div>
                  {roster.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {roster.map(p => (
                        <button key={p.id} onClick={() => onPlayerClick(p)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full px-2 py-0.5 transition-colors">
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-center justify-between gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-gray-400">
                      Coach:
                      <GroupCoachSelect group={g} coaches={coaches} onReassign={onReassignGroup} />
                    </label>
                    <button onClick={() => onOpenGroup(g.id)} className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:bg-blue-50 rounded px-2.5 py-1 font-medium transition-colors">
                      Edit group
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Groups needing a coach — assign the current coach in one click */}
      {unassignedGroups.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
            Needs a coach ({unassignedGroups.length})
          </h3>
          <div className="space-y-2">
            {unassignedGroups.map(g => {
              const tc = TIER_COLORS[g.tier] || TIER_COLORS["No Group"];
              const { startTime, duration } = getGroupSchedule(g);
              return (
                <div key={g.id} className="border border-dashed border-amber-300 bg-amber-50/40 rounded-lg p-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-xs font-semibold ${tc.text} ${tc.bg} border ${tc.border} rounded px-1.5 py-0.5`}>{g.tier}</span>
                    <span className="text-sm font-medium text-gray-700 truncate">{g.day} · {formatRange(startTime, duration)}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:inline">{g.court || "No court"}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => onOpenGroup(g.id)} className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-2 py-1">Details</button>
                    <button onClick={() => onReassignGroup(g.id, coach.id)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white rounded px-2.5 py-1 font-medium transition-colors">
                      Assign to {coach.name.split(" ")[0]}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Availability */}
      <div>
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Availability</h3>
        {availabilitySummary(coach) ? (
          <p className="text-sm text-gray-600">{availabilitySummary(coach)}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">Available anytime (no limits set)</p>
        )}
      </div>

      {/* Notes */}
      {coach.notes && (
        <div>
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Notes</h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{coach.notes}</p>
        </div>
      )}
    </div>
  );
}

export default function CoachesView({
  players, groups, coaches,
  onAddCoach, onUpdateCoach, onDeleteCoach, onPlayerClick,
  movePlayer, updateGroup, removeFromGroup, deleteGroup, onWarnings,
}) {
  const [selectedId, setSelectedId] = useState(coaches[0]?.id || null);
  const [mode, setMode] = useState("view"); // "view" | "add" | "edit"
  const [openGroupId, setOpenGroupId] = useState(null);
  // Set when the user just added a coach: addCoach() assigns the id, so we
  // can't know it here — instead flag that the next coaches change should
  // select whichever coach is newest (last in the array).
  const selectNewest = useRef(false);

  const s = computeStats(players, groups, coaches);
  const statById = Object.fromEntries(s.coachStats.map(cs => [cs.coach.id, cs]));

  // Keep a valid selection as coaches are added/removed.
  useEffect(() => {
    if (coaches.length === 0) { setSelectedId(null); return; }
    if (selectNewest.current) {
      selectNewest.current = false;
      setSelectedId(coaches[coaches.length - 1].id);
      return;
    }
    if (!coaches.some(c => c.id === selectedId)) setSelectedId(coaches[0].id);
  }, [coaches, selectedId]);

  const byDayThenTime = (a, b) => {
    const da = ALL_DAYS.indexOf(a.day), db = ALL_DAYS.indexOf(b.day);
    if (da !== db) return (da < 0 ? 99 : da) - (db < 0 ? 99 : db);
    return (parseTime(getGroupSchedule(a).startTime) || 0) - (parseTime(getGroupSchedule(b).startTime) || 0);
  };

  const selected = coaches.find(c => c.id === selectedId) || null;
  const selectedGroups = selected
    ? groups.filter(g => g.coachId === selected.id).sort(byDayThenTime)
    : [];
  // Scheduled groups with no coach — offered for one-click assignment in the
  // detail panel.
  const unassignedGroups = groups.filter(g => isScheduled(g) && !g.coachId && g.kind !== "openplay").sort(byDayThenTime);

  // Other coaches with room under their cap — shown as a "balance the load"
  // hint when the selected coach is over capacity.
  const spareCoaches = selected
    ? s.coachStats
        .filter(cs => cs.coach.id !== selected.id && cs.coach.maxHours != null && cs.hours < cs.coach.maxHours)
        .map(cs => ({ coach: cs.coach, free: cs.coach.maxHours - cs.hours }))
        .sort((a, b) => b.free - a.free)
    : [];

  const openGroup = openGroupId ? groups.find(g => g.id === openGroupId) || null : null;

  function handleAdd(data) {
    selectNewest.current = true;
    onAddCoach(data);
    setMode("view");
  }

  function handleEditSave(data) {
    onUpdateCoach(selected.id, data);
    setMode("view");
  }

  function handleReassignGroup(groupId, coachId) {
    updateGroup(groupId, { coachId });
  }

  // GroupModal roster handlers, mirroring GroupsView — the open group is
  // re-derived from live `groups` each render, so no local copy to sync.
  function handleAddPlayerToGroup(player) {
    if (!openGroup) return;
    const warnings = getWarnings(player, openGroup);
    if (warnings.length > 0) onWarnings(warnings);
    movePlayer(player.id, player.groupId, openGroup.id);
  }

  function handleRemovePlayerFromGroup(player) {
    if (!openGroup) return;
    removeFromGroup(player.id, openGroup.id);
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-gray-800">Coaches / Pros</h1>
          <p className="text-xs text-gray-500">
            {coaches.length} coach{coaches.length !== 1 ? "es" : ""}
            {s.unassignedGroupCount > 0 && <span className="text-amber-600"> · {s.unassignedGroupCount} group{s.unassignedGroupCount !== 1 ? "s" : ""} need a coach ({fmtHours(s.unassignedGroupHours)}h)</span>}
          </p>
        </div>
        {mode !== "add" && (
          <button onClick={() => setMode("add")} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded transition-colors">+ Add coach</button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[16rem_1fr] gap-4">
        {/* List */}
        <div className="space-y-1.5">
          {coaches.length === 0 ? (
            <p className="text-sm text-gray-400 italic px-1">No coaches yet.</p>
          ) : (
            coaches.map(c => {
              const cs = statById[c.id];
              const active = c.id === selectedId && mode !== "add";
              return (
                <button
                  key={c.id}
                  onClick={() => { setSelectedId(c.id); setMode("view"); }}
                  className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${active ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-gray-50"}`}
                >
                  <CoachAvatar coach={c} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-800 truncate">{c.name}</span>
                    <span className="block text-xs text-gray-500">
                      {fmtHours(cs?.hours || 0)}h · {cs?.groupCount || 0} group{(cs?.groupCount || 0) !== 1 ? "s" : ""}
                    </span>
                  </span>
                  {(cs?.overCapacity || cs?.doubleBooked) && <span className="text-red-500 text-xs flex-shrink-0" title="Has a scheduling warning">⚠</span>}
                </button>
              );
            })
          )}
        </div>

        {/* Detail / form */}
        <div className="border border-gray-200 rounded-xl bg-white p-5 min-h-[24rem]">
          {mode === "add" ? (
            <>
              <h2 className="text-base font-bold text-gray-800 mb-4">New coach</h2>
              <CoachForm coach={null} coachCount={coaches.length} days={ALL_DAYS} onSubmit={handleAdd} onCancel={() => setMode("view")} />
            </>
          ) : mode === "edit" && selected ? (
            <>
              <h2 className="text-base font-bold text-gray-800 mb-4">Edit coach</h2>
              <CoachForm coach={selected} coachCount={coaches.length} days={ALL_DAYS} onSubmit={handleEditSave} onCancel={() => setMode("view")} />
            </>
          ) : selected ? (
            <CoachDetail
              coach={selected}
              stat={statById[selected.id]}
              coachGroups={selectedGroups}
              unassignedGroups={unassignedGroups}
              spareCoaches={spareCoaches}
              coaches={coaches}
              players={players}
              onEdit={() => setMode("edit")}
              onDelete={onDeleteCoach}
              onPlayerClick={onPlayerClick}
              onReassignGroup={handleReassignGroup}
              onOpenGroup={setOpenGroupId}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <div className="text-4xl mb-3">🎾</div>
              <p className="text-sm">No coach selected.</p>
              <button onClick={() => setMode("add")} className="mt-3 text-sm text-blue-600 hover:underline">Add your first coach</button>
            </div>
          )}
        </div>
      </div>

      {openGroup && (openGroup.kind ? (
        <WeekendSessionModal
          mode="edit"
          session={openGroup}
          players={players}
          coaches={coaches}
          onUpdate={(id, data) => updateGroup(id, data)}
          onDelete={id => { deleteGroup(id); setOpenGroupId(null); }}
          onClose={() => setOpenGroupId(null)}
        />
      ) : (
        <GroupModal
          // Keyed by group id: the detail panel can swap `openGroupId` straight
          // from one group to another, and without this the second group would
          // inherit the first's unsaved notes/schedule drafts.
          key={openGroup.id}
          group={openGroup}
          groupPlayers={openGroup.playerIds.map(id => players.find(p => p.id === id)).filter(Boolean)}
          allPlayers={players}
          allGroups={groups}
          coaches={coaches}
          onClose={() => setOpenGroupId(null)}
          onPlayerClick={onPlayerClick}
          onAddPlayer={handleAddPlayerToGroup}
          onRemovePlayer={handleRemovePlayerFromGroup}
          onDeleteGroup={groupId => { deleteGroup(groupId); setOpenGroupId(null); }}
          onUpdateGroup={updateGroup}
        />
      ))}
    </div>
  );
}
